import React, { Component } from "react";
import { observer } from "mobx-react";
import { AxiosResponse } from "axios";
import CourseCategoriesStore from "../../../stores/CourseCategoriesStore";
import CourseCategoriesReactions from "../../../stores/CourseCategoriesStore/CourseCategoriesReactions";
import {
  CourseCategoriesResource,
  CourseCategoryResource,
} from "../../../stores/CourseCategoriesStore/Types";
import { CourseResource } from "../../../stores/CoursesStore/Types";
import {
  EnrolmentsResource,
  OngoingCourse,
} from "../../../stores/EnrolmentStore/Types";
import { getRequest } from "../../../utils/httpRequest";
import CourseFilterBar from "./CourseFilterBar";
import CourseTile from "./CourseTile";
import { AiCoursePicker } from "../../Home/components";

interface Props {
  kind?: string;
  headerTitle?: string;
  userIsInUk: boolean;
}

type CourseFilter = "all" | "bestseller" | "new" | "upcoming";

interface State {
  selectedCategory: string;
  selectedFilter: CourseFilter;
  searchTerm: string;
  ongoingCourses: EnrolmentsResource;
  // Starts true so the picker is there from the first paint. It only goes away
  // once we know for certain there is nothing left for it to suggest.
  hasCoursesLeftToTake: boolean;
}

const isUpcoming = (course: CourseResource) =>
  course.content_version === "upcoming";

const isNew = (course: CourseResource) =>
  !isUpcoming(course) &&
  Boolean(course.new_course || course.went_live_in_the_past_month);

const isBestSeller = (course: CourseResource) =>
  !isUpcoming(course) && Boolean(course.best_seller);

const matchesFilter = (course: CourseResource, filter: CourseFilter) => {
  switch (filter) {
    case "bestseller":
      return isBestSeller(course);
    case "new":
      return isNew(course);
    case "upcoming":
      return isUpcoming(course);
    default:
      // Upcoming courses have their own section at the foot of the page, so
      // they stay out of the skill area grids rather than appearing twice.
      return !isUpcoming(course);
  }
};

// A course can sit in more than one skill area, so anything that flattens the
// categories has to drop the repeats.
const uniqueCourses = (categories: CourseCategoriesResource) => {
  const seen = new Set<number>();

  return categories.reduce<CourseResource[]>((courses, category) => {
    category.courses.forEach((course) => {
      if (seen.has(course.id)) return;

      seen.add(course.id);
      courses.push(course);
    });

    return courses;
  }, []);
};

class CoursesView extends Component<Props, State> {
  courseCategoriesStore = new CourseCategoriesStore();
  isMountedComponent = false;
  lastScrolledHash = "";

  constructor(props) {
    super(props);

    this.state = {
      selectedCategory: "all",
      selectedFilter: "all",
      searchTerm: "",
      ongoingCourses: [],
      hasCoursesLeftToTake: true,
    };
  }

  componentDidMount() {
    this.isMountedComponent = true;
    this.takeOverScrollRestoration();
    this.courseCategoriesStore.loading();
    this.loadOngoingCourses();
    this.loadRemainingCourses();
    window.addEventListener("hashchange", this.scrollToHashCategory);
  }

  componentDidUpdate() {
    this.scrollToHashCategory();
  }

  componentWillUnmount() {
    this.isMountedComponent = false;
    window.removeEventListener("hashchange", this.scrollToHashCategory);

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "auto";
    }
  }

  // Refreshing part-way down this page used to land somewhere else entirely,
  // usually much further down. The courses arrive after the first paint, so at
  // the moment the browser restores the saved pixel offset the page is a
  // fraction of its final height; the offset it restores to means something
  // quite different once the list fills in.
  //
  // Taking restoration over and starting at the top is the honest behaviour for
  // a reload here. A link to #bestsellers still scrolls, since that runs off
  // the hash rather than a remembered offset, and restoration is handed back on
  // the way out so the rest of the site keeps the browser's own behaviour.
  takeOverScrollRestoration = () => {
    if (!("scrollRestoration" in window.history)) return;

    window.history.scrollRestoration = "manual";

    if (this.hashId()) return;

    window.scrollTo(0, 0);
  };

  loadOngoingCourses = () => {
    getRequest("/api/profile/ongoing.json")
      .then((response: AxiosResponse<EnrolmentsResource>) => {
        if (!this.isMountedComponent) {
          return;
        }

        this.setState({ ongoingCourses: response.data || [] });
      })
      .catch(() => {
        if (!this.isMountedComponent) {
          return;
        }

        this.setState({ ongoingCourses: [] });
      });
  };

  // The picker exists to help someone choose their next course, so it has
  // nothing to offer a learner who has taken everything. This endpoint already
  // drops whatever they have enrolled in or passed, and unlike the category
  // payload it is worked out per request rather than served from a shared cache.
  loadRemainingCourses = () => {
    getRequest<CourseResource[], void>("/api/dashboard_courses/profile.json")
      .then((response: AxiosResponse<CourseResource[]>) => {
        if (!this.isMountedComponent) return;

        const left = (response.data || []).filter(
          (course) => !isUpcoming(course) && !course.sample,
        );

        this.setState({ hasCoursesLeftToTake: left.length > 0 });
      })
      .catch(() => {
        // Leave the picker in place; it is the less surprising failure.
      });
  };

  onCategoryChange = (selectedCategory: string) => {
    this.setState({ selectedCategory });
  };

  onFilterChange = (selectedFilter: CourseFilter) => {
    this.setState({ selectedFilter });
  };

  // The curated strip and the coming soon section are only shown on the
  // unfiltered page. Once someone narrows things down, the grid below is
  // already answering the question they asked.
  isBrowsingEverything = () =>
    this.state.selectedFilter === "all" &&
    this.state.selectedCategory === "all" &&
    this.state.searchTerm.trim() === "";

  hashId = () => {
    const { hash } = window.location;

    if (!hash) {
      return "";
    }

    try {
      return decodeURIComponent(hash.replace("#", ""));
    } catch {
      return hash.replace("#", "");
    }
  };

  scrollToHashCategory = () => {
    const hashId = this.hashId();

    if (!hashId || this.lastScrolledHash === hashId) {
      return;
    }

    const target = document.getElementById(hashId);

    if (!target) {
      return;
    }

    this.lastScrolledHash = hashId;

    window.requestAnimationFrame(() => {
      target.scrollIntoView({ block: "start" });
    });

    window.setTimeout(() => {
      target.scrollIntoView({ block: "start" });
    }, 250);
  };

  onSearchChange = (searchTerm: string) => {
    this.setState({ searchTerm });
  };

  courseMatchesSearch = (course: CourseResource) => {
    const { searchTerm } = this.state;
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return true;
    }

    return [
      course.name,
      course.preview_mini_description,
      course.tile_description,
      course.thought_leader_name,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query);
  };

  visibleCategories = (
    courseCategories: CourseCategoriesResource,
  ): CourseCategoriesResource => {
    const { selectedCategory } = this.state;

    return courseCategories
      .filter((category) => {
        return selectedCategory === "all" || category.slug === selectedCategory;
      })
      .map((category) => ({
        ...category,
        courses: category.courses
          .filter((course) => matchesFilter(course, this.state.selectedFilter))
          .filter(this.courseMatchesSearch),
      }))
      .filter((category) => category.courses.length > 0);
  };

  visibleCourseCount = (courseCategories: CourseCategoriesResource) => {
    return courseCategories.reduce((count, category) => {
      return count + category.courses.length;
    }, 0);
  };

  categoryNumber = (index: number) => {
    const number = index + 1;

    return number < 10 ? `0${number}` : `${number}`;
  };

  renderCategoryTitle = (name: string) => {
    const parts = name.split(" & ");

    if (parts.length > 1) {
      const emphasis = parts.pop();

      return (
        <>
          {parts.join(" & ")} &amp; <em>{emphasis}</em>
        </>
      );
    }

    const words = name.split(" ");

    if (words.length > 1) {
      const emphasis = words.pop();

      return (
        <>
          {words.join(" ")} <em>{emphasis}</em>
        </>
      );
    }

    return <>{name}</>;
  };

  ongoingCourseFor = (course: CourseResource): OngoingCourse | undefined => {
    return this.state.ongoingCourses?.find((ongoingCourse) => {
      return ongoingCourse?.course?.id === course?.id;
    });
  };

  renderCourse = (course: CourseResource) => {
    return (
      <CourseTile
        course={course}
        key={course.id}
        ongoingCourse={this.ongoingCourseFor(course)}
        userIsInUk={this.props.userIsInUk}
      />
    );
  };

  renderCategory = (category: CourseCategoryResource, index: number) => (
    <div
      className="category-block"
      data-category={category.slug}
      id={category.slug}
      key={category.id}
    >
      <div className="category-head">
        <span className="category-num">{this.categoryNumber(index)}</span>
        <h2 className="category-title">
          {this.renderCategoryTitle(category.name)}
        </h2>
        <span className="category-count">
          {category.courses.length}{" "}
          {category.courses.length === 1 ? "course" : "courses"}
        </span>
      </div>
      <div className="courses-grid">
        {category.courses.map((course) => this.renderCourse(course))}
      </div>
    </div>
  );

  renderHighlightStrip = (
    courses: CourseResource[],
    id: string,
    title: React.ReactNode,
    blurb: string,
  ) => {
    if (courses.length === 0) return null;

    return (
      <section className={`course-strip course-strip--${id}`} id={id}>
        <div className="container">
          <div className="course-strip__head">
            <h2 className="course-strip__title">{title}</h2>
            <p className="course-strip__blurb">{blurb}</p>
          </div>
          <div className="courses-grid">
            {courses.map((course) => this.renderCourse(course))}
          </div>
        </div>
      </section>
    );
  };

  renderCourses = () => {
    const { userIsInUk } = this.props;
    const { state } = this.courseCategoriesStore;

    switch (state.kind) {
      case "ready": {
        const visibleCategories = this.visibleCategories(
          state.courseCategories,
        );
        const visibleCourseCount = this.visibleCourseCount(visibleCategories);
        const everyCourse = uniqueCourses(state.courseCategories);
        const showExtras = this.isBrowsingEverything();

        return (
          <>
            <CourseFilterBar
              categories={state.courseCategories}
              courses={everyCourse}
              selectedCategory={this.state.selectedCategory}
              selectedFilter={this.state.selectedFilter}
              searchTerm={this.state.searchTerm}
              resultCount={visibleCourseCount}
              onCategoryChange={this.onCategoryChange}
              onFilterChange={this.onFilterChange}
              onSearchChange={this.onSearchChange}
            />

            {showExtras &&
              this.renderHighlightStrip(
                everyCourse.filter(isBestSeller),
                "bestsellers",
                <>
                  Our <em>Bestsellers</em>
                </>,
                "The ones our learners reach for most.",
              )}

            <section className="courses" aria-label="Course catalogue">
              <div className="container">
                {visibleCategories.map((category, index) => (
                  <React.Fragment key={category.id}>
                    {this.renderCategory(category, index)}
                    {index === 1 && this.state.hasCoursesLeftToTake && (
                      <AiCoursePicker
                        heading={
                          this.state.ongoingCourses.length > 0
                            ? "Not sure what to take next?"
                            : undefined
                        }
                        userIsInUk={userIsInUk}
                      />
                    )}
                  </React.Fragment>
                ))}

                {visibleCourseCount === 0 && (
                  <div
                    className="empty-state show"
                    id="empty-state"
                    aria-live="polite"
                  >
                    <h3>No courses match your search.</h3>
                    <p>Try a different keyword, or clear your filters.</p>
                  </div>
                )}
              </div>
            </section>

            {showExtras &&
              this.renderHighlightStrip(
                everyCourse.filter(isUpcoming),
                "coming-soon",
                <>
                  Still in the <em>oven</em>
                </>,
                "Not ready yet, but worth knowing about.",
              )}
          </>
        );
      }
      case "loading":
        return (
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        );
      default:
        return (
          <>
            <CourseFilterBar
              categories={[]}
              courses={[]}
              selectedCategory={this.state.selectedCategory}
              selectedFilter={this.state.selectedFilter}
              searchTerm={this.state.searchTerm}
              resultCount={0}
              onCategoryChange={this.onCategoryChange}
              onFilterChange={this.onFilterChange}
              onSearchChange={this.onSearchChange}
            />
          </>
        );
    }
  };

  render() {
    return (
      <>
        {this.renderCourses()}
        <CourseCategoriesReactions store={this.courseCategoriesStore} />
      </>
    );
  }
}

export default observer(CoursesView);
